import { useRef, useState } from "react";
import { adminApi } from "./adminApi";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X } from "lucide-react";

export default function ImageUploader({ label, value, onChange, hint, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await adminApi.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) upload(file);
    e.target.value = "";
  };

  const clear = () => onChange("");

  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1.5 flex items-stretch gap-3">
        <div className="h-20 w-28 rounded-lg border border-border bg-secondary/40 overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            <img src={value} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testid || 'image'}-upload-btn`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-primary text-white text-sm font-medium hover:bg-[#059669] disabled:opacity-70"
            >
              <Upload className="h-4 w-4" />
              {uploading ? "Uploading…" : value ? "Replace" : "Upload from device"}
            </button>
            {value && (
              <button
                type="button"
                onClick={clear}
                data-testid={`${testid || 'image'}-clear-btn`}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-border text-sm text-muted-foreground hover:text-destructive hover:border-destructive/40"
              >
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
          <Input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste an image URL"
            className="h-9 text-xs"
            data-testid={`${testid || 'image'}-url-input`}
          />
        </div>
      </div>
      {hint && <span className="mt-1 block text-xs text-muted-foreground/70">{hint}</span>}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={onPick}
        data-testid={`${testid || 'image'}-input`}
      />
    </label>
  );
}
